import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listSuspendedUsers, setUserSuspended } from "@/lib/incidents.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/suspensions")({
  head: () => ({ meta: [{ title: "Suspensions — Admin Console" }] }),
  component: AdminSuspensions,
});

type Row = {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string | null;
  is_suspended: boolean | null;
  suspended_at: string | null;
  suspension_reason: string | null;
};

const ROLE_OPTIONS = ["all", "bartender", "venue", "admin"] as const;
type RoleFilter = (typeof ROLE_OPTIONS)[number];

function AdminSuspensions() {
  const load = useServerFn(listSuspendedUsers);
  const setSuspended = useServerFn(setUserSuspended);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);

  const [role, setRole] = useState<RoleFilter>("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await load();
      setRows(r.users as Row[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load suspensions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    const fromTs = from ? new Date(from).getTime() : null;
    const toTs = to ? new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1 : null;
    return rows.filter((r) => {
      if (role !== "all" && (r.role ?? "") !== role) return false;
      if (kw) {
        const hay = [r.suspension_reason, r.full_name, r.email].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      if (fromTs || toTs) {
        if (!r.suspended_at) return false;
        const ts = new Date(r.suspended_at).getTime();
        if (fromTs && ts < fromTs) return false;
        if (toTs && ts > toTs) return false;
      }
      return true;
    });
  }, [rows, role, q, from, to]);

  const lift = async (id: string, name: string) => {
    const reason = window.prompt(`Lift suspension for ${name || "this user"}? Optional note for the audit log:`, "");
    if (reason === null) return;
    setActingId(id);
    try {
      await setSuspended({ data: { user_id: id, suspended: false, ...(reason.trim() ? { reason: reason.trim() } : {}) } });
      toast.success("Suspension lifted. User notified.");
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not lift suspension.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Admin Console</p>
        <h1 className="mt-3 font-display text-3xl">Suspended Accounts</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Review every currently suspended bartender or venue, search by reason, filter by role and date,
          and lift suspensions when appropriate. Every action is recorded in the suspension audit log
          and the affected user is emailed automatically.
        </p>

        {/* Filters */}
        <section className="mt-8 grid gap-4 border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs tracking-wide-luxury text-muted-foreground">Role</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`border px-3 py-1.5 text-xs tracking-wide-luxury capitalize transition ${role === r ? "border-accent text-accent" : "border-border hover:border-accent/50"}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="kw" className="text-xs tracking-wide-luxury text-muted-foreground">Reason keyword</Label>
            <Input id="kw" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. cancellation, no-show, fraud" className="mt-2 rounded-none" />
          </div>
          <div>
            <Label htmlFor="from" className="text-xs tracking-wide-luxury text-muted-foreground">Suspended from</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-2 rounded-none" />
          </div>
          <div>
            <Label htmlFor="to" className="text-xs tracking-wide-luxury text-muted-foreground">Suspended to</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-2 rounded-none" />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Showing <span className="text-accent">{filtered.length}</span> of {rows.length} suspended account{rows.length === 1 ? "" : "s"}.
            </span>
            {(role !== "all" || q || from || to) && (
              <button
                onClick={() => { setRole("all"); setQ(""); setFrom(""); setTo(""); }}
                className="text-xs tracking-wide-luxury text-accent hover:text-accent/80"
              >
                Clear filters
              </button>
            )}
          </div>
        </section>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-10 border border-border bg-card p-8 text-center">
            <p className="font-display text-lg">
              {rows.length === 0 ? "No active suspensions." : "No suspensions match your filters."}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {rows.length === 0 ? "All accounts are currently in good standing." : "Try clearing or widening your filters."}
            </p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {filtered.map((u) => (
              <article key={u.id} className="border border-border bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="font-display text-lg">{u.full_name || "Unnamed"}</p>
                    <p className="text-xs text-muted-foreground">{u.email || "—"} · <span className="capitalize">{u.role || "—"}</span></p>
                    <p className="mt-2 text-xs tracking-wide-luxury text-muted-foreground">
                      Suspended {u.suspended_at ? new Date(u.suspended_at).toLocaleString() : "—"}
                    </p>
                    {u.suspension_reason && (
                      <p className="mt-2 max-w-2xl text-sm">
                        <span className="text-muted-foreground">Reason: </span>{u.suspension_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to="/admin/suspension-history/$userId"
                      params={{ userId: u.id }}
                      className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent self-center"
                    >
                      View history
                    </Link>
                    <Button
                      disabled={actingId === u.id}
                      onClick={() => lift(u.id, u.full_name || "")}
                      className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
                    >
                      {actingId === u.id ? "Lifting…" : "Lift suspension"}
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
